import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword, signOut, sendPasswordResetEmail } from 'firebase/auth';
import { auth, googleProvider, AUTH_MODE, initError } from '../config/firebase';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isAuthenticated = !!user;
  const isAdmin = user?.permissions?.includes('*') || false;
  const permissions = useMemo(() => user?.permissions || [], [user]);

  const hasPermission = useCallback((perm) => {
    return permissions.includes('*') || permissions.includes(perm);
  }, [permissions]);

  // Load user profile from backend
  const loadUser = useCallback(async () => {
    try {
      const data = await authService.getMe();
      if (data.registered === false) {
        // First login - auto-register
        await authService.register(data.displayName || data.email);
        const profile = await authService.getMe();
        setUser(profile);
      } else {
        setUser(data);
      }
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) {
        setUser(null);
      } else {
        setError(typeof err.message === 'string' ? err.message : String(err));
      }
    }
  }, []);

  useEffect(() => {
    if (AUTH_MODE === 'bypass') {
      // In bypass mode, backend always returns the bypass admin user
      loadUser().finally(() => setLoading(false));
      return;
    }

    // Firebase mode: listen for auth state changes
    if (!auth) {
      setLoading(false);
      setError(initError || 'Firebase not configured');
      return;
    }

    let unsubscribe;
    try {
      unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        try {
          if (firebaseUser) {
            await loadUser();
          } else {
            setUser(null);
          }
        } catch (err) {
          setError(typeof err.message === 'string' ? err.message : String(err));
        }
        setLoading(false);
      }, (err) => {
        // Firebase auth error observer
        setError(err?.message || String(err));
        setLoading(false);
      });
    } catch (err) {
      setError(err?.message || String(err));
      setLoading(false);
      return;
    }

    // Listen for 401 events from axios interceptor
    const handleUnauthorized = () => {
      if (auth) signOut(auth);
      setUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      if (unsubscribe) unsubscribe();
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [loadUser]);

  const login = async (method, credentials) => {
    if (AUTH_MODE === 'bypass') return;
    if (!auth) throw new Error('Firebase not configured');

    if (method === 'google') {
      await signInWithPopup(auth, googleProvider);
    } else if (method === 'email') {
      await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
    }
  };

  const logout = async () => {
    if (AUTH_MODE === 'bypass') return;
    if (auth) await signOut(auth);
    setUser(null);
  };

  // Send a Firebase password-reset email for the given address.
  // NOTE: delivery depends on the Identity Platform email config; built-in
  // sending can be unreliable until custom SMTP is configured.
  const resetPassword = async (email) => {
    if (AUTH_MODE === 'bypass') throw new Error('Password reset is unavailable in bypass mode');
    if (!auth) throw new Error('Firebase not configured');
    await sendPasswordResetEmail(auth, email);
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated,
    isAdmin,
    permissions,
    authMode: AUTH_MODE,
    login,
    logout,
    resetPassword,
    hasPermission
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
